import DrawingBase from '../drawing.base'
import { scaledCoordinate } from '../drawing.helper'
import { convertToTimeZone } from '../../../../util/dateTime'
import { getNextValidTradingDay } from '../../../../util/tradingCalendar'
import { DrawingOptions, DrawingSettingGroup, DrawingTypes, SettingValues } from '../drawing.interface'
import { ChartEvents } from '../../chart.events'
import { MouseEventParams, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import { NewsArticle } from '../../../../proto/generated/market_news'
import { drawLightningBolt } from './lightningIcon'
import { constructCalendarDataLookup, getTradingSessionsForDate } from '../../../../util/chart'
import { TimeBucket_TimeSeries } from '../../../../proto/generated/common'

export type NewsArticleType = 'newsArticle'

export type NewsArticleState = {
    data: Record<number, NewsArticle[]>
}

export type NewsArticleOptions = {
    drawingType: NewsArticleType
}

export type NewsArticleDrawingSettings = {
    data: Record<number, NewsArticle[]>
}

const getColorForNewsProvider = (providerCode: string): string => {
    return '#8B5CF6'
}

class NewsArticleTool extends DrawingBase<NewsArticleOptions, NewsArticleDrawingSettings, NewsArticleState> {
    static getColorForNewsProvider = getColorForNewsProvider

    initialPoint = 0
    private clickedCircles: string | null = null
    private normalizedData: Record<number, NewsArticle[]> = {}
    private isDataNormalized = false

    private findHoveredData(x: number, y: number): NewsArticle[] | null {
        const sx = scaledCoordinate(x, this.horizontalPixelRatio)
        const sy = scaledCoordinate(y, this.verticalPixelRatio)
        const data = this.normalizedData

        if (!data || typeof data !== 'object') {
            return null
        }

        const circleRadius = 10 * window.devicePixelRatio
        const marginBottom = 5 * window.devicePixelRatio
        const canvasHeight = this.canvasHeight - circleRadius - marginBottom
        const primaryData = this._chart.getRenderedPrimaryData()
        const futureData = this._chart.emptyTimeSlots
        const combinedData = [...primaryData, ...futureData].filter((item) => item && item.time).sort((a, b) => (a.time as number) - (b.time as number))

        if (!combinedData) return null

        const companyEventsData = this._chart.dataManager?.companyEventsByDate || {}

        let hoveredArticles: NewsArticle[] = []

        for (const timestamp in data) {
            const groupedArticles = data[timestamp]

            if (Array.isArray(groupedArticles) && groupedArticles.length > 0) {
                const newsTimestamp = Number(timestamp)

                let closestIndex = -1
                let minDifference = Infinity

                for (let i = 0; i < combinedData.length; i++) {
                    const candleTime = combinedData[i].time as number
                    const difference = Math.abs(candleTime - newsTimestamp)
                    if (difference < minDifference) {
                        minDifference = difference
                        closestIndex = i
                    }
                }

                if (closestIndex === -1) continue

                const targetTime = combinedData[closestIndex].time as number
                const xCoordinate = scaledCoordinate(this._chartApi.timeScale().timeToCoordinate(targetTime as Time) as number, this.horizontalPixelRatio)

                if (!xCoordinate) continue

                let yOffset = 0
                const candleDate = new Date(targetTime * 1000)
                candleDate.setHours(0, 0, 0, 0)
                const candleDateTimestamp = candleDate.getTime()

                for (const companyTimestamp in companyEventsData) {
                    const companyEvents = companyEventsData[companyTimestamp]
                    if (Array.isArray(companyEvents) && companyEvents.length > 0) {
                        const companyDate = new Date(Number(companyTimestamp))
                        companyDate.setHours(0, 0, 0, 0)
                        if (companyDate.getTime() === candleDateTimestamp) {
                            yOffset = companyEvents.length * (circleRadius + 30)
                            break
                        }
                    }
                }

                const yCoordinate = canvasHeight - yOffset
                const dx = sx - xCoordinate
                const dy = sy - yCoordinate
                const distance = Math.sqrt(dx * dx + dy * dy)

                if (distance <= circleRadius) {
                    hoveredArticles.push(...groupedArticles)
                }
            }
        }

        return hoveredArticles.length > 0 ? hoveredArticles : null
    }

    constructor(options: DrawingOptions<any, any, any>) {
        super(options)

        this._chartApi.subscribeClick(this.handleChartClick.bind(this))
        this._chartApi.timeScale().subscribeVisibleTimeRangeChange(() => {
            this._chart.publishEvent(ChartEvents.NEWS_ARTICLE_CLICKED, null)
            this.clickedCircles = null
        })
    }

    private normalizeData() {
        if (!this.isDataNormalized && this._state.data) {
            this.normalizedData = this.normalizeNewsArticles(this._state.data)
            this.isDataNormalized = true
        }
    }

    private handleChartClick(param: MouseEventParams): void {
        const { point } = param
        if (!point) return

        const clickedData = this.findHoveredData(point.x, point.y)
        if (clickedData && clickedData.length > 0) {
            const data = this.normalizedData
            let clickedTimestamp = ''
            for (const timestamp in data) {
                const articles = data[timestamp]
                if (articles.some((article) => clickedData.includes(article))) {
                    clickedTimestamp = timestamp
                    break
                }
            }

            if (this.clickedCircles === clickedTimestamp) {
                this.clickedCircles = null
                this._chart.publishEvent(ChartEvents.NEWS_ARTICLE_CLICKED, null)
            } else {
                this.clickedCircles = clickedTimestamp
                this._chart.publishEvent(ChartEvents.NEWS_ARTICLE_CLICKED, {
                    x: point.x,
                    y: point.y - 10,
                    articles: clickedData,
                    type: this.getDrawingType()
                })
            }
        } else {
            this._chart.publishEvent(ChartEvents.NEWS_ARTICLE_CLICKED, null)
            this.clickedCircles = null
        }
    }

    private getNewsDisplayDate(originalDate: Date): Date | null {
        const tradingCalendarData = this._chart.tradingCalendarData
        if (!tradingCalendarData) {
            return originalDate
        }
        const isOneDayMode = this._chart.timeBucket.timeSeries === TimeBucket_TimeSeries.DAY

        if (isOneDayMode) {
            const istSeconds = convertToTimeZone(originalDate.getTime() / 1000, this._chart.timezone)
            const istDate = new Date(istSeconds * 1000)

            const istYear = istDate.getUTCFullYear()
            const istMonth = istDate.getUTCMonth()
            const istDay = istDate.getUTCDate()

            const dayStartInIST = new Date(Date.UTC(istYear, istMonth, istDay, 0, 0, 0, 0))

            return dayStartInIST
        }

        const holidays = tradingCalendarData.holidays.map((h) => h.date) || []
        const specialTradingDays = tradingCalendarData.specialTradingDays || []
        const dateStr = (d: Date) => d.toISOString().split('T')[0]
        const isHoliday = (d: Date) => holidays.includes(dateStr(d))
        const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6
        const isSpecialTradingDay = (d: Date) => specialTradingDays.some((st) => st.date === dateStr(d) && st.sessionTypes.length > 0)

        const istSeconds = convertToTimeZone(originalDate.getTime() / 1000, this._chart.timezone)

        const istDate = new Date(istSeconds * 1000)
        const istYear = istDate.getUTCFullYear()
        const istMonth = istDate.getUTCMonth()
        const istDay = istDate.getUTCDate()
        const istHours = istDate.getUTCHours()
        const istMinutes = istDate.getUTCMinutes()
        const istSecondsInDay = istHours * 3600 + istMinutes * 60 + istDate.getUTCSeconds()

        const calendarCheckDate = new Date(istYear, istMonth, istDay)

        const isTradingDay = isSpecialTradingDay(calendarCheckDate) || (!isHoliday(calendarCheckDate) && !isWeekend(calendarCheckDate))

        if (isTradingDay) {
            const calendarDataLookup = constructCalendarDataLookup(tradingCalendarData)
            const tradingSessions = getTradingSessionsForDate(Math.floor(istSeconds), calendarDataLookup)

            if (tradingSessions && tradingSessions.length > 0) {
                const isWithinTradingHours = tradingSessions.some((session) => istSecondsInDay >= session.startTime && istSecondsInDay <= session.endTime)

                if (isWithinTradingHours) {
                    return istDate
                }

                const firstSession = tradingSessions[0]
                if (istSecondsInDay < firstSession.startTime) {
                    const startHours = Math.floor(firstSession.startTime / 3600)
                    const startMinutes = Math.floor((firstSession.startTime % 3600) / 60)
                    const startSeconds = firstSession.startTime % 60

                    return new Date(istYear, istMonth, istDay, startHours, startMinutes, startSeconds, 0)
                }
            }
        }
        let nextDay = new Date(calendarCheckDate)
        nextDay.setDate(nextDay.getDate() + 1)

        const nextTradingDay = getNextValidTradingDay(nextDay, tradingCalendarData)
        if (!nextTradingDay) return null

        const calendarDataLookup = constructCalendarDataLookup(tradingCalendarData)
        const nextDayTradingSessions = getTradingSessionsForDate(Math.floor(nextTradingDay.getTime() / 1000), calendarDataLookup)

        if (nextDayTradingSessions && nextDayTradingSessions.length > 0) {
            const firstSession = nextDayTradingSessions[0]
            const startHours = Math.floor(firstSession.startTime / 3600)
            const startMinutes = Math.floor((firstSession.startTime % 3600) / 60)
            const startSeconds = firstSession.startTime % 60

            nextTradingDay.setHours(startHours, startMinutes, startSeconds, 0)
        } else {
            nextTradingDay.setHours(9, 15, 0, 0)
        }

        return nextTradingDay
    }

    normalizeNewsArticles(data: any) {
        const updated: any = {}

        for (const key in data) {
            const group = data[key]

            for (const article of group) {
                let articleTimestamp = article.created
                if (articleTimestamp > 2000000000) {
                    articleTimestamp = Math.floor(articleTimestamp / 1000)
                }
                const originalDate = new Date(articleTimestamp * 1000)

                if (isNaN(originalDate.getTime())) {
                    continue
                }
                const year = originalDate.getFullYear()
                if (year < 1970 || year > 2100) {
                    continue
                }

                const displayDate = this.getNewsDisplayDate(originalDate)
                if (!displayDate) {
                    continue
                }

                const timestampInSeconds = Math.floor(displayDate.getTime() / 1000)

                const updatedArticle = {
                    ...article,
                    created: articleTimestamp
                }

                if (!updated[timestampInSeconds]) {
                    updated[timestampInSeconds] = []
                }
                updated[timestampInSeconds].push(updatedArticle)
            }
        }

        return updated
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context

        this.normalizeData()
        const data = this.normalizedData

        if (!data) {
            return
        }

        const visibleLogicalRange = this._chartApi.timeScale().getVisibleLogicalRange()

        if (!visibleLogicalRange) {
            return
        }

        const barsInfo = this.getSeries().barsInLogicalRange(visibleLogicalRange)

        if (!barsInfo) {
            return
        }

        const devicePixelRatio = window.devicePixelRatio || 1
        const baseSize = 10
        const circleRadius = baseSize * devicePixelRatio
        const marginBottom = 5 * devicePixelRatio
        const canvasHeight = this.canvasHeight - circleRadius - marginBottom
        const adjustedFontSize = baseSize * devicePixelRatio

        const primaryData = this._chart.getRenderedPrimaryData()
        const futureData = this._chart.emptyTimeSlots
        const combinedData = [...primaryData, ...futureData].filter((item) => item && item.time).sort((a, b) => (a.time as number) - (b.time as number))

        const companyEventsData = this._chart.dataManager?.companyEventsByDate || {}

        Object.keys(data).forEach((key) => {
            const articles = data[Number(key)]

            if (Array.isArray(articles) && articles.length === 0) {
                return
            }

            if (!Array.isArray(articles) || articles.length === 0) {
                return
            }

            const newsTimestamp = Number(key)

            let closestIndex = -1
            let minDifference = Infinity

            for (let i = 0; i < combinedData.length; i++) {
                const candleTime = combinedData[i].time as number
                const difference = Math.abs(candleTime - newsTimestamp)
                if (difference < minDifference) {
                    minDifference = difference
                    closestIndex = i
                }
            }

            if (closestIndex === -1) {
                return
            }

            if (closestIndex < visibleLogicalRange.from || closestIndex > visibleLogicalRange.to) {
                return
            }

            const targetTime = combinedData[closestIndex].time as number
            const timeCoordinate = this._chartApi.timeScale().timeToCoordinate(targetTime as Time)

            if (!timeCoordinate) {
                return
            }

            const xCoordinate = scaledCoordinate(timeCoordinate, this.horizontalPixelRatio)

            if (!xCoordinate) {
                return
            }

            let yOffset = 0
            const candleDate = new Date(targetTime * 1000)
            candleDate.setHours(0, 0, 0, 0)
            const candleDateTimestamp = candleDate.getTime()

            let hasCompanyEvents = false
            for (const companyTimestamp in companyEventsData) {
                const companyEvents = companyEventsData[companyTimestamp]
                if (Array.isArray(companyEvents) && companyEvents.length > 0) {
                    const companyDate = new Date(Number(companyTimestamp))
                    companyDate.setHours(0, 0, 0, 0)
                    if (companyDate.getTime() === candleDateTimestamp) {
                        hasCompanyEvents = true
                        yOffset = companyEvents.length * (circleRadius + 30)
                        break
                    }
                }
            }

            const yCoordinate = canvasHeight - yOffset
            const firstArticle = articles[0]
            const articleKey = `${key}` // Use timestamp as key for grouping
            const isClicked = this.clickedCircles === articleKey

            ctx.save()
            ctx.translate(xCoordinate, yCoordinate)

            ctx.beginPath()
            ctx.arc(0, 0, circleRadius + 2, 0, Math.PI * 2)
            ctx.strokeStyle = this._chart.theme.palette.background.paper
            ctx.lineWidth = 2
            ctx.stroke()
            ctx.closePath()

            ctx.beginPath()
            ctx.arc(0, 0, circleRadius, 0, Math.PI * 2)
            ctx.strokeStyle = getColorForNewsProvider(firstArticle.providerCode)
            ctx.lineWidth = 3
            ctx.stroke()
            ctx.closePath()

            ctx.beginPath()
            ctx.arc(0, 0, circleRadius - 2, 0, Math.PI * 2)
            ctx.fillStyle = isClicked ? getColorForNewsProvider(firstArticle.providerCode) : this._chart.theme.palette.background.paper
            ctx.fill()
            ctx.closePath()

            ctx.fillStyle = isClicked ? 'white' : getColorForNewsProvider(firstArticle.providerCode)
            ctx.font = `bold ${adjustedFontSize}px Arial`
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            const fillColor = isClicked ? 'white' : getColorForNewsProvider(firstArticle.providerCode)
            drawLightningBolt(ctx, 0, 0, circleRadius * 2.0, fillColor)

            ctx.restore()
        })
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const hoveredData = this.findHoveredData(x, y)
        if (hoveredData && hoveredData.length > 0) {
            this._hovered = true

            const primaryData = this._chart.getRenderedPrimaryData()
            const futureData = this._chart.emptyTimeSlots
            const combinedData = [...primaryData, ...futureData].filter((item) => item && item.time).sort((a, b) => (a.time as number) - (b.time as number))

            const targetCandle = combinedData[this.initialPoint]
            const timestamp = (targetCandle.time as number) * 1000

            this._chart.setCrossHairPosition(targetCandle.customValues, timestamp)
            const highlightColor = getColorForNewsProvider(hoveredData[0].providerCode)

            this._chartApi.applyOptions({
                crosshair: {
                    vertLine: {
                        color: highlightColor,
                        visible: true
                    },
                    horzLine: {
                        visible: false
                    }
                }
            })

            return {
                externalId: this.id,
                zOrder: 'top',
                cursorStyle: 'pointer'
            }
        } else if (this._hovered) {
            this._hovered = false

            this._chartApi.applyOptions({
                crosshair: {
                    vertLine: {
                        color: '#758696',
                        visible: true
                    },
                    horzLine: {
                        visible: true
                    }
                }
            })
        }

        return null
    }

    onSettingsUpdate = (settings: Partial<NewsArticleDrawingSettings>) => {
        if (settings.data) {
            this._state.data = settings.data
            // Reset normalization when data changes
            this.isDataNormalized = false
            this.normalizeData()
        }
        this._settings = {
            ...this._settings,
            ...settings
        }

        this.primitive?.requestUpdate()
    }

    getAvailableSettings = (): DrawingSettingGroup<NewsArticleDrawingSettings, SettingValues>[] => {
        return []
    }

    getDrawingType(): DrawingTypes {
        switch (this.options.drawingType) {
            case 'newsArticle':
                return 'newsArticle'
        }
    }

    getSettingsValues(): NewsArticleDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        return []
    }

    loadIntialState() {
        super.loadIntialState()
        this.normalizeData()
    }
}

export default NewsArticleTool